import { searchClient } from "algolia-instant-search";

const { autocomplete, getAlgoliaFacets, getAlgoliaResults } =
  window["@algolia/autocomplete-js"];
const { recommendClient } = window["@algolia/recommend"];
const { createRedirectUrlPlugin } =
  window["@algolia/autocomplete-plugin-redirect-url"];

const PRODUCTS_INDEX_NAME = "shopify_products_manual_index_prod";
const ARTICLES_INDEX_NAME = "shopify_articles_manual_index_prod";
const QUERY_SUGGESTIONS_INDEX_NAME = "shopify_diesel_query_suggestions";
const BRANDS_INDEX_NAME = "shopify_brands_manual_index_prod";
const PRODUCTS_ORDER_COUNT_DESC_REPLICA =
  "shopify_products_manual_index_prod_order_count_desc";

const METAFIELD_BRAND_ATTRIBUTE = "metafield_brand";
const RELATED_BRANDS_LIMIT = 5;

const QUERY_DEBOUNCE_MS = 300;
const STALL_THRESHOLD_MS = 500;
const recommendationsClient = recommendClient(
  searchClient.appId,
  searchClient.apiKey,
);

// Ported from this theme's own `formatMoney` (assets/theme.js, inside the
// `multiCurrency` IIFE — not exposed on a global, so re-implemented locally
// here rather than reaching into that script). Takes the money format and
// cents amount; the format string already carries the currency symbol/code.
function formatMoney(cents, format) {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || "{{amount}}";
  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = ".",
  ) {
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    const parts = number.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`,
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }
  return formatString.replace(placeholderRegex, value);
}

function debounce(callback, delay) {
  let timeout;
  let pendingResolvers = [];

  return (...args) =>
    new Promise((resolve) => {
      pendingResolvers.push(resolve);
      if (timeout) clearTimeout(timeout);

      timeout = setTimeout(() => {
        const result = callback(...args);
        const resolvers = pendingResolvers;
        pendingResolvers = [];
        timeout = undefined;
        resolvers.forEach((pendingResolve) => pendingResolve(result));
      }, delay);
    });
}

if (!customElements.get("algolia-autocomplete")) {
  class FyreAlgoliaAutocomplete extends HTMLElement {
    initialized = false;
    autocompleteApi;
    resizeObserver;
    observedHeaderWidth;
    query = "";
    searchUrl = "";
    totalHits = 0;
    moneyFormat = "{{amount}}";
    currency = "USD";
    catalogIds;
    // Algolia exposes no getter for panel state, so it is mirrored here from
    // `onStateChange` for `handleOutsidePointerDown` to read.
    autocompleteIsOpen = false;
    outsideClickArmed = false;
    listenerAbort;
    hasRedirectPluginLoaded = false;
    redirectCache = new Map();
    redirectUrlPlugin;

    connectedCallback() {
      if (this.initialized) return;

      const searchUrl = this.dataset.searchUrl;
      if (!searchUrl) {
        console.warn(
          "[WARNING] algolia-autocomplete: missing required `data-search-url`",
        );
        return;
      }

      const container = this.querySelector(
        "[data-algolia-autocomplete-container]",
      );
      if (!container) {
        console.warn(
          "[WARNING] algolia-autocomplete: missing required `[data-algolia-autocomplete-container]` element",
        );
        return;
      }

      this.searchUrl = searchUrl;

      this.moneyFormat = this.dataset.moneyFormat ?? this.moneyFormat;
      this.currency = this.dataset.currency ?? this.currency;
      this.catalogIds = this.dataset.catalogIds;
      this.redirectUrlPlugin = createRedirectUrlPlugin();

      this.autocompleteApi = autocomplete({
        container,
        panelContainer: container,
        panelPlacement: "input-wrapper-width",
        detachedMediaQuery: "not all",
        insights: true,
        openOnFocus: true,
        // shouldPanelOpen: () => true,
        placeholder: this.dataset.placeholder ?? "Search",
        stallThreshold: STALL_THRESHOLD_MS,
        classNames: {
          root: "tw:w-full",
          form: "tw:relative tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-racetech-btn-search tw:border tw:border-racetech-border tw:bg-racetech-white tw:px-3 tw:py-2 tw:focus-within:border-blue-500 tw:focus-within:ring-1 tw:focus-within:ring-blue-500",
          inputWrapper: "tw:min-w-0 tw:flex-1",
          inputWrapperSuffix: "tw:flex",
          input:
            "tw:w-full tw:bg-transparent tw:font-racetech-primary tw:text-sm tw:text-racetech-text tw:outline-none tw:placeholder:text-racetech-medium-grey",
          submitButton: "tw:shrink-0 tw:text-racetech-medium-dark",
          clearButton: "tw:shrink-0 tw:text-racetech-medium-dark tw:sm:pl-3",
          panel: `tw:min-h-0 tw:!w-full ${this.panelHeightClassNames} tw:overflow-y-auto tw:overscroll-contain tw:select-text`,
          panelLayout: "autocomplete-panel-layout",
          source: "autocomplete-panel-source",
          list: "tw:m-0 tw:grid tw:[grid-template-areas:none] tw:list-none tw:!grid-cols-1 tw:gap-2 tw:p-0 tw:[[data-autocomplete-source-id=categories]_&]:!grid-cols-2 tw:[[data-autocomplete-source-id=categories]_&]:hz:!grid-cols-6 tw:[[data-autocomplete-source-id=products]_&]:!grid-cols-2 tw:[[data-autocomplete-source-id=products]_&]:auto-rows-fr tw:[[data-autocomplete-source-id=brands]_&]:!flex tw:[[data-autocomplete-source-id=brands]_&]:!flex-row tw:[[data-autocomplete-source-id=brands]_&]:!flex-wrap tw:[[data-autocomplete-source-id=brands]_&]:hz:!grid tw:[[data-autocomplete-source-id=brands]_&]:hz:!grid-cols-1 tw:[[data-autocomplete-source-id=guides]_&]:gap-0",
          item: "tw:list-none tw:[[data-autocomplete-source-id=brands]_&]:shrink-0 tw:[[data-autocomplete-source-id=brands]_&]:first:w-full tw:[[data-autocomplete-source-id=brands]_&:has(.autocomplete-related-brands-heading)]:contents tw:[&[aria-selected=true]>a]:outline tw:[&[aria-selected=true]>a]:outline-2 tw:[&[aria-selected=true]>a]:outline-racetech-primary tw:[&[aria-selected=true]>button]:outline tw:[&[aria-selected=true]>button]:outline-2 tw:[&[aria-selected=true]>button]:outline-racetech-primary",
        },
        getInputProps: ({ props, setIsOpen }) => ({
          ...props,
          "aria-describedby": "algolia-autocomplete-hint",
          onFocus: (event) => {
            if (this.query) {
              setIsOpen(true);
              return;
            }
            props.onFocus(event);
          },
        }),
        getPanelProps: ({ props }) => ({ ...props, onMouseDown: () => {} }),
        getItemProps: ({ props }) => ({
          ...props,
          onClick: (e) => {
            // Close panel right away now that suggestions redirect
            this.autocompleteApi.setIsOpen(false);
            props.onClick(e);
          },
        }),
        plugins: [this.redirectUrlPlugin],
        onSubmit: ({ state }) => {
          if (!state.query.trim()) return;

          const redirectUrl = this.hasRedirectPluginLoaded
            ? this.redirectCache.get(state.query)
            : undefined;
          window.location.assign(redirectUrl ?? this.getSearchUrl(state.query));
        },
        onReset: ({ refresh }) => {
          queueMicrotask(refresh);
        },
        onStateChange: ({ prevState, state }) => {
          if (state.isOpen && !prevState.isOpen) {
            requestAnimationFrame(() => this.updateCenterOffset());
          }

          this.autocompleteIsOpen = state.isOpen;

          this.hasRedirectPluginLoaded =
            state.status !== "loading" && !!state.context?.redirectUrlPlugin;
          const redirectUrl =
            state.context?.redirectUrlPlugin?.data?.[0]?.urls?.[0];
          if (redirectUrl) this.redirectCache.set(state.query, redirectUrl);

          this.updateSearchHint({ state });
        },
        getSources: ({ query }) => {
          this.query = query;
          this.totalHits = 0;

          const sources = query.trim()
            ? [
                this.suggestionsSource,
                this.categoriesSource,
                this.brandsSource,
                this.productsSource,
                this.guidesSource,
              ]
            : [
                this.popularSearchesSource,
                this.topCategoriesSource,
                this.trendingProductsSource,
                this.unqueriedGuidesSource,
              ];

          return sources;
        },
        render: this.renderPanel,
        renderNoResults: this.renderNoResultsPanel,
      });

      requestAnimationFrame(() => {
        this.updateFieldHeight();
        this.updateCenterOffset();
      });

      this.resizeObserver = new ResizeObserver(([entry]) => {
        const width = entry?.contentRect.width;
        if (!width || !this.isRendered) return;

        this.updateFieldHeight();
        this.updateCenterOffset();

        if (
          this.observedHeaderWidth !== undefined &&
          this.observedHeaderWidth !== width
        ) {
          this.autocompleteApi?.setIsOpen(false);
        }
        this.observedHeaderWidth = width;
      });
      this.resizeObserver.observe(
        document.getElementById("theme-header") ?? this,
      );

      this.listenerAbort = new AbortController();
      const { signal } = this.listenerAbort;
      window.addEventListener("pointerdown", this.handleOutsidePointerDown, {
        capture: true,
        signal,
      });
      window.addEventListener("click", this.handleOutsideClick, {
        capture: true,
        signal,
      });

      this.initialized = true;
    }

    get panelHeightClassNames() {
      return "tw:max-h-[calc(100dvh_-_var(--header-height)_-_var(--autocomplete-field-height)_-_2rem)] tw:max-hz:max-h-[calc(100dvh_-_var(--header-height)_-_var(--autocomplete-field-height))]";
    }

    // Algolia closes the panel from its own window `mousedown`/`touchstart`
    // listeners, which run before the click that follows. `pointerdown` precedes
    // both, and capture on window precedes Algolia's bubble handlers, so this is
    // the last point where `autocompleteIsOpen` still reflects the gesture's intent.
    handleOutsidePointerDown = (event) => {
      const target = event.target;
      this.outsideClickArmed =
        this.autocompleteIsOpen &&
        !(target instanceof Node && this.contains(target));
    };

    handleOutsideClick = (event) => {
      if (!this.outsideClickArmed) return;

      this.outsideClickArmed = false;
      event.preventDefault();
      event.stopPropagation();
    };

    /* ===================== Panel renderers ==================== */

    renderPanel = (parameters, root) => {
      if (
        parameters.state.status === "loading" ||
        parameters.state.status === "stalled"
      ) {
        this.renderLoadingPanel(parameters, root);
        return;
      }

      if (!parameters.state.query.trim()) {
        this.renderEmptyPanel(parameters, root);
        return;
      }

      this.renderQueriedPanel(parameters, root);
    };

    renderLoadingPanel = ({ html, render }, root) => {
      const panel = html`
        <div
          class="tw:mt-2.5 tw:border-t tw:border-racetech-border"
          data-autocomplete-state="${"loading"}"
          aria-busy="true"
          aria-live="polite"
        >
          <span class="tw:sr-only">Loading search results</span>
          <div
            class="tw:animate-pulse tw:border-b tw:border-racetech-border tw:px-3 tw:py-3 tw:sm:px-4"
            aria-hidden="true"
          >
            <div
              class="tw:h-4 tw:w-2/5 tw:rounded tw:bg-racetech-light-grey tw:sm:w-1/4"
            ></div>
          </div>
          <div
            class="autocomplete-panel-layout tw:animate-pulse"
            aria-hidden="true"
          >
            <section
              class="autocomplete-panel-source"
              data-autocomplete-source-id="suggestions"
            >
              <div
                class="tw:mb-2 tw:h-3 tw:w-24 tw:rounded tw:bg-racetech-light-grey"
              ></div>
              <div class="tw:flex tw:flex-wrap tw:gap-2">
                <div
                  class="tw:h-8 tw:w-24 tw:rounded tw:bg-racetech-light-grey"
                ></div>
                <div
                  class="tw:h-8 tw:w-32 tw:rounded tw:bg-racetech-light-grey"
                ></div>
                <div
                  class="tw:h-8 tw:w-20 tw:rounded tw:bg-racetech-light-grey"
                ></div>
                <div
                  class="tw:h-8 tw:w-28 tw:rounded tw:bg-racetech-light-grey"
                ></div>
              </div>
            </section>
            <section
              class="autocomplete-panel-source"
              data-autocomplete-source-id="categories"
            >
              <div
                class="tw:mb-3 tw:h-4 tw:w-36 tw:rounded tw:bg-racetech-light-grey"
              ></div>
              <div class="tw:grid tw:grid-cols-2 tw:gap-2 tw:hz:grid-cols-6">
                <div class="tw:h-14 tw:rounded tw:bg-racetech-light-grey"></div>
                <div class="tw:h-14 tw:rounded tw:bg-racetech-light-grey"></div>
                <div class="tw:h-14 tw:rounded tw:bg-racetech-light-grey"></div>
                <div class="tw:h-14 tw:rounded tw:bg-racetech-light-grey"></div>
                <div class="tw:h-14 tw:rounded tw:bg-racetech-light-grey"></div>
                <div class="tw:h-14 tw:rounded tw:bg-racetech-light-grey"></div>
              </div>
            </section>
            <section
              class="autocomplete-panel-source"
              data-autocomplete-source-id="brands"
            >
              <div class="tw:h-28 tw:rounded tw:bg-racetech-light-grey"></div>
              <div
                class="tw:mt-3 tw:h-3 tw:w-28 tw:rounded tw:bg-racetech-light-grey"
              ></div>
              <div class="tw:mt-2 tw:flex tw:flex-wrap tw:gap-2">
                <div
                  class="tw:h-8 tw:w-24 tw:rounded tw:bg-racetech-light-grey"
                ></div>
                <div
                  class="tw:h-8 tw:w-28 tw:rounded tw:bg-racetech-light-grey"
                ></div>
              </div>
            </section>
            <section
              class="autocomplete-panel-source"
              data-autocomplete-source-id="products"
            >
              <div
                class="tw:mb-3 tw:h-4 tw:w-28 tw:rounded tw:bg-racetech-light-grey"
              ></div>
              <div class="tw:grid tw:grid-cols-2 tw:gap-2">
                <div
                  class="tw:h-48 tw:rounded tw:bg-racetech-light-grey tw:hz:h-56"
                ></div>
                <div
                  class="tw:h-48 tw:rounded tw:bg-racetech-light-grey tw:hz:h-56"
                ></div>
                <div
                  class="tw:h-48 tw:rounded tw:bg-racetech-light-grey tw:hz:h-56"
                ></div>
                <div
                  class="tw:h-48 tw:rounded tw:bg-racetech-light-grey tw:hz:h-56"
                ></div>
              </div>
            </section>
            <section
              class="autocomplete-panel-source"
              data-autocomplete-source-id="guides"
            >
              <div
                class="tw:mb-2 tw:h-4 tw:w-24 tw:rounded tw:bg-racetech-light-grey"
              ></div>
              <div class="tw:space-y-2">
                <div class="tw:h-10 tw:rounded tw:bg-racetech-light-grey"></div>
                <div class="tw:h-10 tw:rounded tw:bg-racetech-light-grey"></div>
              </div>
            </section>
          </div>
        </div>
      `;

      render(panel, root);
    };

    renderNoResultsPanel = (parameters, root) => {
      const { html, render, state } = parameters;

      if (state.status === "loading" || state.status === "stalled") {
        this.renderLoadingPanel(parameters, root);
        return;
      }

      if (!state.query.trim()) {
        this.renderEmptyPanel(parameters, root);
        return;
      }

      this.updateSearchHint({ state, noResults: true });

      const panel = html`
        <div
          class="tw:mt-2.5 tw:border-t tw:border-racetech-border"
          data-autocomplete-state="${"no-results"}"
        >
          <div class="tw:px-3 tw:py-8 tw:text-center tw:sm:px-4 tw:sm:py-10">
            <p
              class="tw:font-racetech-primary tw:text-sm tw:font-racetech-bold tw:text-racetech-dark"
            >
              No results for “${state.query}”
            </p>
            <p
              class="tw:mt-1 tw:font-racetech-primary tw:text-sm tw:text-racetech-medium-dark"
            >
              Try another search.
            </p>
          </div>
        </div>
      `;

      render(panel, root);
    };

    renderQueriedPanel = ({ children, html, render, state }, root) => {
      const itemCountFor = (sourceId) =>
        state.collections.find(({ source }) => source.sourceId === sourceId)
          ?.items.length ?? 0;
      const shownHits = itemCountFor("products");
      const totalHits = this.totalHits;
      const resultsSummary = `${shownHits} of ${totalHits} results`;
      const isColumnEmpty =
        itemCountFor("brands") === 0 && itemCountFor("guides") === 0;

      const panel = html`
        <div
          class="tw:mt-2.5 tw:border-t tw:border-racetech-border"
          data-autocomplete-state="${"queried"}"
          data-autocomplete-left-column="${isColumnEmpty ? "empty" : "filled"}"
        >
          <div
            class="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:border-b tw:border-racetech-border tw:px-3 tw:py-3 tw:sm:px-4"
          >
            <a
              class="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-2 tw:font-racetech-primary tw:text-xs tw:tracking-wide tw:text-racetech-medium-grey tw:max-hz:cursor-pointer"
              href="${this.getSearchUrl(state.query)}"
            >
              <span class="tw:font-racetech-bold tw:uppercase"
                >Results for</span
              >
              <strong
                class="tw:text-lg tw:leading-none tw:font-racetech-bold tw:text-racetech-dark"
                >“${state.query}”</strong
              >
              <span class="tw:normal-case">— ${resultsSummary}</span>
            </a>
            <a
              class="tw:inline-flex tw:shrink-0 tw:items-center tw:gap-1 tw:font-racetech-primary tw:text-xs tw:font-racetech-bold tw:uppercase tw:tracking-wide tw:text-racetech-primary tw:no-underline tw:hover:text-racetech-brembo-red tw:max-hz:hidden"
              href="${this.getSearchUrl(state.query)}"
            >
              View all ${totalHits} results
              <span class="autocomplete-chevron" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  class="tw:size-5 tw:fill-none tw:stroke-current tw:sm:size-[14px]"
                  stroke-width="2"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </span>
            </a>
          </div>
          ${children}
          <div
            class="tw:border-t tw:border-racetech-border tw:px-3 tw:py-3 tw:sm:px-4"
          >
            <a
              class="autocomplete-view-all-button"
              href="${this.getSearchUrl(state.query)}"
              tabindex="-1"
            >
              <span class="tw:text-center">
                View all ${totalHits} results
                <span class="tw:max-hz:hidden">for “${state.query}”</span>
              </span>
            </a>
          </div>
        </div>
      `;

      render(panel, root);
    };

    renderEmptyPanel = ({ children, html, render }, root) => {
      const panel = html`
        <div
          class="tw:mt-2.5 tw:border-t tw:border-racetech-border"
          data-autocomplete-state="${"empty"}"
        >
          ${children}
        </div>
      `;

      render(panel, root);
    };

    /* ==================== Queried sources ==================== */

    get suggestionsSource() {
      const query = this.query;

      return {
        sourceId: "suggestions",
        getItemInputValue: ({ item }) => item.query,
        onSelect: ({ item }) => {
          window.location.assign(this.getSearchUrl(item.query));
        },
        getItems: () => this.getSuggestionItems(query),
        templates: {
          header: ({ html }) =>
            html`<h2 class="autocomplete-suggestions-heading">Suggestions</h2>`,
          item: this.renderSuggestionItem,
        },
      };
    }

    get categoriesSource() {
      const query = this.query;

      return {
        sourceId: "categories",
        getItemUrl: ({ item }) => this.getCategoryUrl(item),
        getItems: () => this.getCategoryItemsDebounced(query),
        templates: {
          header: ({ html }) =>
            html`<h2 class="autocomplete-section-heading">
              Shop categories in ${query}
            </h2>`,
          item: this.renderCategoryItem,
        },
      };
    }

    get brandsSource() {
      const query = this.query;

      return {
        sourceId: "brands",
        getItemUrl: ({ item }) => this.getBrandUrl(item),
        getItems: () => this.getBrandItemsDebounced(query),
        templates: {
          item: this.renderBrandItem,
        },
      };
    }

    get productsSource() {
      const query = this.query;

      return {
        sourceId: "products",
        getItemUrl: ({ item }) => this.getProductUrl(item),
        getItems: () => this.getProductItemsDebounced(query),
        templates: {
          header: ({ html }) => html`
            <div class="autocomplete-products-header">
              <h2 class="autocomplete-section-heading">Top products</h2>
              <span
                class="tw:font-racetech-primary tw:text-xs tw:text-racetech-medium-grey tw:max-hz:hidden"
              >
                Sorted by relevance
              </span>
            </div>
          `,
          item: this.renderProductItem,
        },
      };
    }

    get guidesSource() {
      const query = this.query;

      return {
        sourceId: "guides",
        getItemUrl: ({ item }) => this.getGuideUrl(item),
        getItems: () => this.getGuideItemsDebounced(query),
        templates: {
          header: ({ html }) => html`
            <h2
              class="autocomplete-section-heading autocomplete-guides-heading"
            >
              Related guides
            </h2>
          `,
          item: this.renderGuideItem,
        },
      };
    }

    /* ================== Empty-query sources ================== */

    get popularSearchesSource() {
      return {
        sourceId: "suggestions",
        // getItemInputValue: ({ item }) => item.query,
        onSelect: ({ item, setIsOpen }) => {
          window.location.assign(this.getSearchUrl(item.query));
        },
        getItems: this.getPopularSearchItems,
        templates: {
          header: ({ html }) =>
            html`<h2 class="autocomplete-suggestions-heading">
              Popular searches
            </h2>`,
          item: this.renderSuggestionItem,
        },
      };
    }

    get topCategoriesSource() {
      return {
        sourceId: "categories",
        getItemUrl: ({ item }) => this.getCategoryUrl(item),
        getItems: () => this.getCategoryItems(""),
        templates: {
          header: ({ html }) =>
            html`<h2 class="autocomplete-section-heading">
              Shop top categories
            </h2>`,
          item: this.renderCategoryItem,
        },
      };
    }

    get trendingProductsSource() {
      return {
        sourceId: "products",
        getItemUrl: ({ item }) => this.getProductUrl(item),
        getItems: this.getTrendingProductItems,
        templates: {
          header: ({ html }) => html`
            <div class="autocomplete-products-header">
              <h2 class="autocomplete-section-heading">Trending products</h2>
              <span
                class="tw:font-racetech-primary tw:text-xs tw:text-racetech-medium-grey tw:max-hz:hidden"
              >
                Popular right now
              </span>
            </div>
          `,
          item: this.renderProductItem,
        },
      };
    }

    get unqueriedGuidesSource() {
      return {
        sourceId: "guides",
        getItemUrl: ({ item }) => this.getGuideUrl(item),
        getItems: this.getUnqueriedGuideItems,
        templates: {
          header: ({ html }) => html`
            <h2
              class="autocomplete-section-heading autocomplete-guides-heading"
            >
              Guides
            </h2>
          `,
          item: this.renderGuideItem,
        },
      };
    }

    /* ============ Source templates.item renderers ============ */

    renderSuggestionItem = ({ item: suggestion, components, html }) => {
      return html`
        <button type="button" class="autocomplete-suggestion-button">
          <span class="tw:min-w-0"
            >${components.Highlight({
              hit: suggestion,
              attribute: "query",
            })}</span
          >
          <span class="autocomplete-suggestion-arrow" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              class="tw:size-4 tw:fill-none tw:stroke-current"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M17 17 7 7M7 7h8M7 7v8" />
            </svg>
          </span>
        </button>
      `;
    };

    renderCategoryItem = ({ item: category, html }) => {
      return html`
        <a
          class="autocomplete-result-card"
          href="${this.getCategoryUrl(category)}"
        >
          <span
            class="tw:text-sm tw:leading-4 tw:font-racetech-bold tw:text-racetech-dark"
            >${category.label}</span
          >
          <span
            class="tw:mt-1 tw:text-xs tw:italic tw:font-racetech-semibold tw:text-racetech-medium-dark"
          >
            ${category.count} parts
          </span>
        </a>
      `;
    };

    renderBrandItem = ({ item: brand, html }) => {
      const brandUrl = this.getBrandUrl(brand);

      if (brand.topBrand) {
        return html`
          <a class="autocomplete-top-brand-card" href="${brandUrl}">
            <h2
              class="tw:text-[10px] tw:leading-none tw:font-racetech-bold tw:uppercase tw:tracking-wide tw:text-racetech-primary tw:hz:text-xs tw:hz:leading-normal"
            >
              Top brand match
            </h2>
            <span class="autocomplete-top-brand-name">${brand.label}</span>
            <span class="autocomplete-top-brand-details">
              <span class="tw:text-racetech-medium-light"
                >${brand.products_count} parts available</span
              >
              <span
                class="tw:flex tw:items-center tw:gap-2 tw:font-racetech-bold tw:uppercase tw:tracking-wide"
              >
                Shop all
                <span class="tw:max-hz:hidden">${brand.label}</span>
                <span
                  class="autocomplete-chevron tw:text-racetech-primary"
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 24 24"
                    class="tw:size-5 tw:fill-none tw:stroke-current tw:hz:size-[18px]"
                    stroke-width="2"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </span>
              </span>
            </span>
          </a>
        `;
      }

      return html`
        ${brand.startsRelated
          ? html`<h2 class="autocomplete-related-brands-heading">
              Related brands
            </h2>`
          : ""}
        <a class="autocomplete-brand-chip" href="${brandUrl}">
          <span>${brand.label}</span>
          <span class="autocomplete-brand-count"
            >${brand.products_count} parts</span
          >
        </a>
      `;
    };

    renderProductItem = ({ item: product, components, html }) => {
      const isDiscontinued =
        String(product.metafield_product_availability || "")
          .trim()
          .toLowerCase() === "discontinued";
      const price =
        typeof product.price === "number"
          ? formatMoney(Math.round(product.price * 100), this.moneyFormat)
          : "";
      const compareAtPriceAmount = Number(product.compare_at_price);
      const compareAtPrice =
        compareAtPriceAmount > 0
          ? formatMoney(
              Math.round(compareAtPriceAmount * 100),
              this.moneyFormat,
            )
          : "";
      const reviewCount =
        typeof product.metafield_reviews_count === "string" &&
        product.metafield_reviews_count.trim()
          ? Number(product.metafield_reviews_count)
          : Number.NaN;
      const averageRating = product.metafield_reviews_average;
      const hasRating = typeof averageRating === "number";
      const roundedRating = hasRating
        ? Math.min(5, Math.max(0, Math.round(averageRating)))
        : 0;
      const reviewStars = hasRating
        ? "★".repeat(roundedRating) + "☆".repeat(5 - roundedRating)
        : "";
      const hasReviewCount = Number.isFinite(reviewCount);

      return html`
        <a
          class="tw:group autocomplete-product-card"
          href="${this.getProductUrl(product)}"
        >
          <span
            class="autocomplete-product-media tw:relative tw:flex tw:aspect-[4/3] tw:items-center tw:justify-center tw:overflow-hidden tw:bg-racetech-offwhite"
          >
            ${product.featured_image?.url
              ? html`<img
                  class="tw:size-full tw:object-contain tw:transition-transform tw:duration-200 tw:group-hover:scale-[1.05]"
                  src="${product.featured_image.url}"
                  alt="${product.featured_image.alt ?? product.title}"
                  width="320"
                  height="240"
                />`
              : html`<svg
                  viewBox="0 0 24 24"
                  class="tw:size-9 tw:fill-none tw:stroke-racetech-border"
                  stroke-width="1.5"
                  aria-hidden="true"
                >
                  <path d="m12 3 7.25 4.2v9.6L12 21l-7.25-4.2V7.2L12 3Z" />
                  <path d="m12 8.5 3 1.75v3.5L12 15.5 9 13.75v-3.5l3-1.75Z" />
                </svg>`}
            ${isDiscontinued
              ? html`<span
                  class="autocomplete-product-badge autocomplete-product-badge--discontinued"
                  style="position: absolute; top: 0; left: 0; width: 100%; box-sizing: border-box; z-index: 1; background: var(--primary-color, #c00); color: #fff; padding: 3px 6px; font-size: 12px; line-height: 1.4; text-align: center;"
                  >DISCONTINUED</span
                >`
              : product.metafield_quick_ship === "1" ||
            product.metafield_expert_pick === "1"
              ? html`<span
                  class="tw:absolute tw:top-1 tw:left-1 tw:flex tw:flex-col tw:items-start tw:gap-1"
                >
                  ${product.metafield_quick_ship === "1"
                    ? html`<span
                        class="autocomplete-product-badge autocomplete-product-badge--quick-ship"
                        >Quick ship</span
                      >`
                    : ""}
                  ${product.metafield_expert_pick === "1"
                    ? html`<span
                        class="autocomplete-product-badge autocomplete-product-badge--expert-pick"
                        >Expert pick</span
                      >`
                    : ""}
                </span>`
              : ""}
          </span>
          <span class="tw:block tw:min-w-0 tw:p-2">
            ${product.sku
              ? html`<span
                  class="tw:block tw:truncate tw:text-[9px] tw:font-racetech-semibold tw:uppercase tw:tracking-wide tw:text-racetech-medium-dark"
                  >SKU #: ${product.sku}</span
                >`
              : ""}
            <span
              class="tw:mt-1 tw:block tw:min-h-10 tw:overflow-hidden tw:text-sm tw:leading-5 tw:font-racetech-bold tw:text-racetech-dark tw:transition-colors tw:group-hover:text-racetech-primary"
            >
              ${components.Highlight({ hit: product, attribute: "title" })}
            </span>
            ${hasRating || hasReviewCount
              ? html`<span
                  class="tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-[10px] tw:font-racetech-semibold tw:text-racetech-medium-dark"
                >
                  ${hasRating
                    ? html`<span
                        class="tw:tracking-tight tw:text-racetech-brembo-red"
                        aria-label="${averageRating.toFixed(1)} out of 5"
                        >${reviewStars}</span
                      >`
                    : ""}
                  ${hasReviewCount
                    ? html`<span
                        >(${new Intl.NumberFormat().format(reviewCount)})</span
                      >`
                    : ""}
                </span>`
              : ""}
            <span
              class="tw:mt-1 tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-1 tw:text-base tw:leading-none"
            >
              <span
                class="tw:font-autocomplete-heading tw:font-racetech-bold tw:text-racetech-dark"
                >${price}</span
              >
              ${compareAtPrice
                ? html`<s
                    class="tw:text-xs tw:font-racetech-light tw:text-[var(--primary-color)]"
                    >${compareAtPrice}</s
                  >`
                : ""}
            </span>
          </span>
        </a>
      `;
    };

    renderGuideItem = ({ item: guide, html }) => {
      const subtitle = this.getGuideSubtitle(guide);

      return html`
        <a class="autocomplete-guide-link" href="${this.getGuideUrl(guide)}">
          <span class="autocomplete-guide-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              class="tw:size-4 tw:fill-none tw:stroke-current"
              stroke-width="1.75"
            >
              <path
                d="M4.5 5.25A2.25 2.25 0 0 1 6.75 3H20v15.75H6.75A2.25 2.25 0 0 0 4.5 21V5.25Z"
              />
              <path d="M4.5 5.25V21M8 7h8M8 10h8" />
            </svg>
          </span>
          <span class="tw:min-w-0 tw:flex-1">
            <span
              class="tw:block tw:text-sm tw:font-racetech-bold tw:text-racetech-dark"
              >${guide.title}</span
            >
            ${subtitle
              ? html`<span
                  class="tw:mt-0.5 tw:block tw:text-xs tw:text-racetech-medium-dark"
                  >${subtitle}</span
                >`
              : ""}
          </span>
          <span
            class="autocomplete-chevron tw:self-center tw:text-racetech-primary tw:hz:hidden"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              class="tw:size-5 tw:fill-none tw:stroke-current tw:hz:size-[18px]"
              stroke-width="2"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </span>
        </a>
      `;
    };

    /* ================ Debounced query loaders ================ */

    getSuggestionItemsDebounced = debounce(
      (query) => this.getSuggestionItems(query),
      QUERY_DEBOUNCE_MS,
    );

    getCategoryItemsDebounced = debounce(
      (query) => this.getCategoryItems(query),
      QUERY_DEBOUNCE_MS,
    );

    getBrandItemsDebounced = debounce(
      (query) => this.getBrandItems(query),
      QUERY_DEBOUNCE_MS,
    );

    getProductItemsDebounced = debounce(
      (query) => this.getProductItems(query),
      QUERY_DEBOUNCE_MS,
    );

    getGuideItemsDebounced = debounce(
      (query) => this.getGuideItems(query),
      QUERY_DEBOUNCE_MS,
    );

    /* ================= Queried item loading ================== */

    getProductsFilter() {
      const base = "status:ACTIVE AND NOT metafield_seo_hidden:1";
      return this.catalogIds
        ? `${base} AND catalog_ids:${this.catalogIds}`
        : base;
    }

    getSuggestionItems(query) {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: QUERY_SUGGESTIONS_INDEX_NAME,
            params: {
              query,
              hitsPerPage: 4,
              clickAnalytics: true,
              attributesToRetrieve: ["query"],
              attributesToSnippet: ["query:10"],
              snippetEllipsisText: "…",
            },
          },
        ],
      });
    }

    getCategoryItems(query) {
      const facetQueryFor = (facet) => ({
        indexName: PRODUCTS_INDEX_NAME,
        type: "facet",
        facet,
        query,
        maxFacetHits: 6,
        params: { distinct: true, filters: this.getProductsFilter() },
      });

      return getAlgoliaFacets({
        searchClient,
        queries: [
          facetQueryFor("product_type"),
          facetQueryFor("metafield_product_subtype"),
        ],
        transformResponse: ({ facetHits }) => {
          const [types = [], subtypes = []] = facetHits;
          const tagged = [
            ...types.map((item) => ({ ...item, facet: "product_type" })),
            ...subtypes.map((item) => ({
              ...item,
              facet: "metafield_product_subtype",
            })),
          ];

          return [
            ...new Map(tagged.map((item) => [item.label, item])).values(),
          ].slice(0, 6);
        },
      });
    }

    groupBestSellersByVendor(bestSellers) {
      const groups = new Map();
      for (const bestSeller of bestSellers ?? []) {
        const vendor = bestSeller.vendor;
        if (!vendor) continue;

        const group = groups.get(vendor) ?? {
          vendor,
          hitCount: 0,
          brandIds: new Set(),
        };
        group.hitCount += 1;

        const brandId = bestSeller[METAFIELD_BRAND_ATTRIBUTE];
        if (brandId) group.brandIds.add(brandId);

        groups.set(vendor, group);
      }

      return groups;
    }

    // Brand whose name is most relevant to query
    getTopBrand(brandHits) {
      const top = brandHits?.find(
        (brand) => brand.collection_handle && brand.collection_products_count,
      );
      if (!top) return;

      return {
        objectID: top.objectID,
        label: top.brand_name,
        handle: top.collection_handle,
        products_count: top.collection_products_count,
      };
    }

    // Ranks vendors by top-30 presence and uses facet counts for displayed totals
    getRelatedBrands(bestSellersByVendor, vendorFacetCounts, topBrandId) {
      return [...bestSellersByVendor.values()]
        .filter(({ brandIds }) => !brandIds.has(topBrandId))
        .sort((a, b) => b.hitCount - a.hitCount)
        .slice(0, RELATED_BRANDS_LIMIT)
        .map(({ vendor, hitCount }) => ({
          label: vendor,
          vendor,
          products_count: vendorFacetCounts?.[vendor] ?? hitCount,
        }));
    }

    getBrandItems(query) {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: BRANDS_INDEX_NAME,
            params: {
              query,
              hitsPerPage: 8,
              removeWordsIfNoResults: "allOptional",
              attributesToRetrieve: [
                "brand_name",
                "collection_handle",
                "collection_products_count",
              ],
            },
          },
          {
            indexName: PRODUCTS_ORDER_COUNT_DESC_REPLICA,
            params: {
              query,
              hitsPerPage: 30,
              distinct: true,
              facets: ["vendor"],
              maxValuesPerFacet: 1000,
              attributesToRetrieve: ["vendor", METAFIELD_BRAND_ATTRIBUTE],
              filters: this.getProductsFilter(),
            },
          },
        ],
        transformResponse: ({ hits, results }) => {
          // Groups the replica's top 30 hits by vendor for related-brand ranking
          const bestSellersByVendor = this.groupBestSellersByVendor(hits[1]);
          const topBrand = this.getTopBrand(hits[0]);
          // Counts all matching products per vendor for the displayed product totals
          const vendorFacetCounts = results[1]?.facets?.vendor;
          const relatedBrands = this.getRelatedBrands(
            bestSellersByVendor,
            vendorFacetCounts,
            topBrand?.objectID,
          );

          return [
            ...(topBrand ? [{ ...topBrand, topBrand: true }] : []),
            ...relatedBrands.map((brand, index) => ({
              ...brand,
              query,
              startsRelated: index === 0,
            })),
          ];
        },
      });
    }

    getProductItems(query) {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: PRODUCTS_INDEX_NAME,
            params: {
              query,
              hitsPerPage: 8,
              distinct: 1,
              filters: this.getProductsFilter(),
              clickAnalytics: true,
              attributesToRetrieve: [
                "handle",
                "title",
                "featured_image",
                "price",
                "compare_at_price",
                "currency_code",
                "sku",
                "metafield_reviews_average",
                "metafield_reviews_count",
                "metafield_quick_ship",
                "metafield_expert_pick",
                "metafield_product_availability",
              ],
            },
          },
        ],
        transformResponse: ({ hits, results }) => {
          const result = results[0];
          this.totalHits =
            result && "nbHits" in result && typeof result.nbHits === "number"
              ? result.nbHits
              : 0;

          // `queryID` is only present on the response when `clickAnalytics`
          // is requested above. Carried on each hit (rather than read off
          // `results` again at click time) so `getProductUrl` can propagate
          // it into the destination URL for downstream conversion
          // attribution — the PDP/cart code reads it back via the
          // `algolia_query_id` param (see `product-form.js`, `cart.js`).
          const queryID =
            result && "queryID" in result ? result.queryID : undefined;
          return (hits[0] ?? []).map((hit) => ({ ...hit, queryID }));
        },
      });
    }

    getGuideItems(query) {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: ARTICLES_INDEX_NAME,
            params: {
              query,
              hitsPerPage: 4,
            },
          },
        ],
      });
    }

    /* =============== Empty-query item loading ================ */

    getPopularSearchItems = () => {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: QUERY_SUGGESTIONS_INDEX_NAME,
            params: {
              query: "",
              hitsPerPage: 6,
              clickAnalytics: true,
              attributesToRetrieve: ["query", "exact_nb_hits"],
              attributesToSnippet: ["query:10"],
              snippetEllipsisText: "…",
            },
          },
        ],
      });
    };

    getTrendingProductItems = async () => {
      const { results } = await recommendationsClient.getRecommendations({
        requests: [
          {
            indexName: PRODUCTS_INDEX_NAME,
            model: "trending-items",
            threshold: 0,
            maxRecommendations: 30,
            queryParameters: {
              filters: this.getProductsFilter(),
              attributesToRetrieve: [
                "handle",
                "title",
                "featured_image",
                "price",
                "compare_at_price",
                "currency_code",
                "sku",
                "metafield_reviews_average",
                "metafield_reviews_count",
                "metafield_quick_ship",
                "metafield_expert_pick",
                "metafield_product_availability",
              ],
            },
          },
        ],
      });

      const products = (results[0]?.hits ?? []).filter(
        (hit) =>
          "handle" in hit &&
          typeof hit.handle === "string" &&
          "title" in hit &&
          typeof hit.title === "string",
      );

      return [
        ...new Map(
          products.map((product) => [product.handle, product]),
        ).values(),
      ];
    };

    getUnqueriedGuideItems = () => {
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName: ARTICLES_INDEX_NAME,
            params: {
              query: "",
              hitsPerPage: 4,
            },
          },
        ],
      });
    };

    /* ============ Navigation and content helpers ============= */

    getSearchUrl(query) {
      const destination = new URL(this.searchUrl, window.location.origin);
      destination.searchParams.set("q", query);
      return destination.toString();
    }

    getProductUrl(product) {
      const queryIdParam = product.queryID
        ? `?algolia_query_id=${product.queryID}`
        : "";
      return `/products/${product.handle}${queryIdParam}`;
    }

    // `[0]` keeps the category facet array-shaped for the refinement-list widget.
    getCategoryUrl(item) {
      const destination = new URL(this.searchUrl, window.location.origin);
      destination.searchParams.set(
        `filter.${item.facet ?? "product_type"}[0]`,
        item.label,
      );
      return destination.toString();
    }

    getGuideUrl(guide) {
      return `/blogs/${this.toHandle(guide.blog_title)}/${guide.handle}`;
    }

    getGuideSubtitle(guide) {
      return (
        this.getTextContent(guide.summary) ||
        this.getTextContent(guide.seo_description)
      );
    }

    getTextContent(value) {
      if (!value) return "";

      const document = new DOMParser().parseFromString(value, "text/html");
      return document.body.textContent?.trim() ?? "";
    }

    toHandle(value) {
      return value
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    getBrandUrl(brand) {
      if (brand.topBrand) return `/collections/${brand.handle}`;

      const destination = new URL(this.searchUrl, window.location.origin);
      destination.searchParams.set("q", brand.query ?? this.query);
      destination.searchParams.set("filter.vendor[0]", brand.vendor);
      return destination.toString();
    }

    /* ================= DOM and accessibility ================= */

    // Hidden instances measure as zero; `offsetParent` supports older browsers.
    get isRendered() {
      return this.checkVisibility?.() ?? this.offsetParent !== null;
    }

    // Publishes the field height because its absolute container adds no layout height.
    updateFieldHeight() {
      const form = this.querySelector("form");
      if (!form || !this.isRendered) return;

      this.style.setProperty(
        "--autocomplete-field-height",
        `${form.offsetHeight}px`,
      );
    }

    // Publishes the offset needed to center the absolute panel in the viewport.
    updateCenterOffset() {
      if (!this.isRendered) return;

      const paddingLeft =
        Number.parseFloat(getComputedStyle(this).paddingLeft) || 0;
      const containingBlockLeft =
        this.getBoundingClientRect().left + paddingLeft;

      this.style.setProperty(
        "--autocomplete-center-offset",
        `${document.documentElement.clientWidth / 2 - containingBlockLeft}px`,
      );
    }

    updateSearchHint({ state, noResults = false }) {
      const hint = this.querySelector("[data-algolia-autocomplete-hint]");
      if (!hint) return;

      const query = state.query.trim();
      const loading = state.status === "loading" || state.status === "stalled";
      if (!query || loading) {
        hint.hidden = true;
        hint.textContent = "";
        return;
      }

      hint.hidden = false;

      if (noResults) {
        hint.textContent = `No results for this search.`;
        return;
      }

      hint.textContent = `Press Enter to view all ${this.totalHits} results.`;
    }

    /* ======================== Cleanup ======================== */

    disconnectedCallback() {
      this.listenerAbort?.abort();
      this.listenerAbort = undefined;
      this.autocompleteIsOpen = false;
      this.outsideClickArmed = false;
      this.autocompleteApi?.destroy();
      this.autocompleteApi = undefined;
      this.resizeObserver?.disconnect();
      this.resizeObserver = undefined;
      this.observedHeaderWidth = undefined;
      this.redirectUrlPlugin = undefined;
      this.redirectCache.clear();
      this.hasRedirectPluginLoaded = false;
      this.initialized = false;
    }
  }

  customElements.define("algolia-autocomplete", FyreAlgoliaAutocomplete);
}
